import {
  backButton, closingBehavior, init, isTMA, miniApp, popup, requestContact, swipeBehavior,
  themeParams, viewport,
} from '@telegram-apps/sdk-react';

/**
 * Тонкая прослойка над SDK: всё, что может отсутствовать вне Telegram, живёт здесь,
 * чтобы остальной код не обвешивался проверками. В обычном браузере функции
 * молча ничего не делают — это основной режим разработки.
 */

let ready = false;

export function isTelegram(): boolean {
  try {
    return isTMA();
  } catch {
    return false;
  }
}

export async function initTelegram(): Promise<void> {
  if (ready || !isTelegram()) return;
  ready = true;

  init();

  await Promise.allSettled([
    miniApp.mount(),
    themeParams.mount(),
    viewport.mount(),
  ]);

  if (backButton.mount.isAvailable()) backButton.mount();
  if (swipeBehavior.mount.isAvailable()) swipeBehavior.mount();
  // Подтверждение закрытия включается точечно — экраном с несохранённой формой.
  if (closingBehavior.mount.isAvailable()) closingBehavior.mount();

  if (miniApp.ready.isAvailable()) miniApp.ready();
  if (viewport.expand.isAvailable()) viewport.expand();
  // Свайп вниз закрывает приложение прямо посреди записи — на длинных экранах это потеря шага.
  if (swipeBehavior.disableVertical.isAvailable()) swipeBehavior.disableVertical();
  // Безопасные зоны в CSS-переменных: --tg-viewport-safe-area-inset-*
  if (viewport.bindCssVars.isAvailable()) viewport.bindCssVars();
}

/** Тему берём из Telegram, вне его — из системной настройки браузера. */
export function preferredDark(): boolean {
  if (isTelegram()) {
    try {
      return miniApp.isDark();
    } catch {
      /* компонент ещё не смонтирован — падаем на системную тему */
    }
  }
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;
}

export function applyTheme(dark: boolean): void {
  document.documentElement.dataset.theme = dark ? 'dark' : 'light';
}

/**
 * Шапка и фон мессенджера красятся под фон приложения — иначе полоса сверху
 * спорит с экраном. Это единственное, для чего берутся themeParams Telegram:
 * сама вёрстка живёт на своей палитре.
 */
export function syncTelegramChrome(): void {
  if (!isTelegram()) return;
  const styles = getComputedStyle(document.documentElement);
  const bg = hex(styles.getPropertyValue('--bg'));
  if (!bg) return;
  try {
    if (miniApp.setHeaderColor.isAvailable() && miniApp.setHeaderColor.supports.rgb()) miniApp.setHeaderColor(bg);
    if (miniApp.setBackgroundColor.isAvailable()) miniApp.setBackgroundColor(bg);
    if (miniApp.setBottomBarColor.isAvailable()) miniApp.setBottomBarColor(bg);
  } catch {
    /* старый клиент не умеет — не повод падать */
  }
}

function hex(value: string): `#${string}` | null {
  const v = value.trim();
  return /^#[0-9a-f]{6}$/i.test(v) ? (v as `#${string}`) : null;
}

// --- BackButton ------------------------------------------------------------

export function showBackButton(handler: () => void): () => void {
  if (!isTelegram() || !backButton.isMounted()) return () => {};
  const off = backButton.onClick(handler);
  backButton.show();
  return () => {
    off();
    backButton.hide();
  };
}

export function hideBackButton(): void {
  if (isTelegram() && backButton.isMounted()) backButton.hide();
}

// --- подтверждения ---------------------------------------------------------

/**
 * Вопрос «да/нет» нативным окном Telegram. Вне Telegram — обычный `confirm`
 * браузера: диалог обязан быть модальным и блокирующим, иначе действие уедет
 * вперёд ответа. Старый клиент без попапов тоже падает на `confirm`.
 *
 * Telegram обрезает заголовок на 64 символах, а текст на 256 — режем сами,
 * чтобы вместо вопроса не прилетело исключение.
 */
export async function showConfirm(message: string, opts: { title?: string; destructive?: boolean } = {}): Promise<boolean> {
  const title = (opts.title ?? '').slice(0, 64);
  const text = message.slice(0, 256);

  if (isTelegram() && popup.show.isAvailable()) {
    try {
      const pressed = await popup.show({
        title,
        message: text,
        buttons: [
          { id: 'no', type: 'cancel' },
          { id: 'yes', type: opts.destructive ? 'destructive' : 'default', text: 'Да' },
        ],
      });
      return pressed === 'yes';
    } catch {
      /* попап уже открыт или клиент не смог — спрашиваем средствами браузера */
    }
  }
  return typeof window.confirm === 'function' && window.confirm(title ? `${title}

${text}` : text);
}

/**
 * Подтверждение при закрытии Mini App свайпом или крестиком. Включается, пока
 * на экране есть несохранённые изменения, и снимается вместе с ними: постоянно
 * висящее подтверждение приучает жать «выйти» не глядя.
 */
export function setClosingConfirmation(on: boolean): void {
  if (!isTelegram() || !closingBehavior.isMounted()) return;
  try {
    if (on) closingBehavior.enableConfirmation();
    else closingBehavior.disableConfirmation();
  } catch {
    /* клиент не умеет — остаётся подтверждение при уходе с экрана */
  }
}

// --- контакт ---------------------------------------------------------------

/**
 * Телефон из Telegram: ноль опечаток и номер сразу верифицирован.
 * Возвращает null, если человек отказался или клиент не умеет — тогда остаётся
 * ручной ввод, и это нормальный путь, а не ошибка.
 */
export async function askContact(): Promise<string | null> {
  if (!isTelegram() || !requestContact.isAvailable()) return null;
  try {
    const res = (await requestContact()) as { parsed?: { contact?: { phone_number?: string } } };
    return res.parsed?.contact?.phone_number ?? null;
  } catch {
    return null;
  }
}

/** Умеет ли клиент отдать телефон сам — от этого зависит, показывать ли кнопку. */
export function canAskContact(): boolean {
  try {
    return isTelegram() && requestContact.isAvailable();
  } catch {
    return false;
  }
}
